import { ReactNode, useState } from 'react';
import { Formik, Form, FormikProps } from 'formik';
import * as Yup from 'yup';
import { wave } from '@round/api';
import { showNotification } from '@/utility/showNotification';
import WaveLogo from '@/ui/WaveLogo/WaveLogo';
import CheckIcon from '@/ui/icons/CheckIcon';
import { LocationValue, emptyLocationValue } from './LocationStep';
import type { InfluencerLocationOption } from '@/hooks/useInfluencerLocationSelect';

export const SUPPORT_EMAIL = 'hello@wave.me';

export type AccountValue = {
    /** Stable synthetic id other sections reference; survives edits and row removals. */
    uid: string;
    platform: wave.CreatorApplicationPlatform;
    handle: string;
    // Per-account section values (clipping); wizards without account sections leave the defaults.
    basedInCountry: InfluencerLocationOption | null;
    audienceTerritories: InfluencerLocationOption[];
    editNiches: string[];
    fanPageArtist: string;
    genres: string[];
    genresOther: string;
    // BCP 47 primary language subtags from the languages section options.
    contentLanguages: string[];
    rate: string;
    rateCurrency: wave.CreatorApplicationRateCurrency;
};
export type TagOption = { value: number; label: string };

// A counter rather than crypto.randomUUID, which jsdom doesn't provide in tests.
let nextAccountUid = 0;

export const emptyAccount = (platform: wave.CreatorApplicationPlatform = 'TIKTOK'): AccountValue => ({
    uid: `account-${++nextAccountUid}`,
    platform,
    handle: '',
    basedInCountry: null,
    audienceTerritories: [],
    editNiches: [],
    fanPageArtist: '',
    genres: [],
    genresOther: '',
    contentLanguages: [],
    rate: '',
    rateCurrency: 'USD',
});

export type BundleOfferValue = {
    numPosts: string;
    price: string;
    currency: wave.CreatorApplicationRateCurrency;
    /** Stable AccountValue.uid references; empty means every account (the backend's own convention). */
    accountUids: string[];
    /** Whole days, 1-14; empty means no expected duration. */
    expectedDurationDays: string;
};

export const emptyBundleOffer = (): BundleOfferValue => ({
    numPosts: '',
    price: '',
    currency: 'USD',
    accountUids: [],
    expectedDurationDays: '',
});

/**
 * A row the applicant never started. The step is optional and opens with one blank row,
 * so such a row passes validation and is dropped from the posted body. Fields are read
 * defensively because formik turns blank strings into `undefined` before validating.
 */
export const isBundleOfferEmpty = (offer: BundleOfferValue): boolean =>
    !offer.numPosts?.trim() &&
    !offer.price?.trim() &&
    !offer.expectedDurationDays?.trim() &&
    !offer.accountUids?.length;

/**
 * The accounts a bundle covers. An empty or fully stale selection falls back to every
 * account, which is what the backend itself reads an empty account list as.
 */
export const bundleOfferAccounts = (offer: BundleOfferValue, accounts: AccountValue[]): AccountValue[] => {
    const selected = accounts.filter((account) => offer.accountUids.includes(account.uid));
    return selected.length > 0 ? selected : accounts;
};

/** Superset of the fields across all signup wizards; each wizard renders a subset. */
export type SignupFormValues = {
    firstName: string;
    lastName: string;
    email: string;
    confirmEmail: string;
    phoneNumber: string;
    isOver18: boolean;
    locationId: number | null;
    happyToTravel: boolean;
    accounts: AccountValue[];
    otherSocials: string;
    contentTags: TagOption[];
    contentFormats: wave.CreatorContentFormat[];
    experienceLevel: wave.CreatorExperienceLevel | '';
    campaignTypes: wave.CreatorCampaignType[];
    commercialNotes: string;
    boundaries: string;
    genres: string[];
    openToCpm: boolean;
    /** UI-only shortcut: mirror the first account's section values onto every account. */
    sameForAllAccounts: boolean;
    notificationChannels: wave.CreatorNotificationChannel[];
    availability: wave.CreatorAvailability[];
    /** Optional package deals, collected only where the signup asks for them. */
    bundleOffers: BundleOfferValue[];
    termsAccepted: boolean;
};

export const signupInitialValues: SignupFormValues = {
    firstName: '',
    lastName: '',
    email: '',
    confirmEmail: '',
    phoneNumber: '',
    isOver18: false,
    locationId: null,
    happyToTravel: false,
    accounts: [emptyAccount()],
    otherSocials: '',
    contentTags: [],
    contentFormats: [],
    experienceLevel: '',
    campaignTypes: [],
    commercialNotes: '',
    boundaries: '',
    genres: [],
    openToCpm: false,
    sameForAllAccounts: false,
    notificationChannels: ['email'],
    availability: [],
    bundleOffers: [emptyBundleOffer()],
    termsAccepted: false,
};

const PLATFORM_DOMAINS: Record<wave.CreatorApplicationPlatform, string[]> = {
    TIKTOK: ['tiktok.com'],
    INSTAGRAM: ['instagram.com', 'instagr.am'],
    YOUTUBE: ['youtube.com', 'youtu.be'],
};

/** Rules for every account row; per-account section rules are merged on top per signup. */
export const baseAccountShape = {
    platform: Yup.string().required(),
    handle: Yup.string()
        .trim()
        .required('Enter a handle or remove this row')
        .max(200, 'This is too long for a single handle')
        .test('single-handle', 'Enter a single handle, not multiple', (value) => !value || !/\s/.test(value.trim()))
        .test('platform-url-match', "This link doesn't match the selected platform", function (value) {
            if (!value) return true;
            const text = value.toLowerCase();
            // A pasted link: explicit protocol/www, any domain followed by a path, or a bare
            // domain ending in a common TLD. The TLD list keeps handles that contain dots
            // (e.g. "john.doe") from being misread as domains and wrongly rejected.
            const looksLikeUrl =
                /https?:\/\/|www\.|\.\w{2,}\//.test(text) ||
                /\.(com|net|org|io|co|tv|me|am|be|app|gg|fm|xyz)([/?#:]|$)/.test(text);
            if (!looksLikeUrl) return true;
            const platform = (this.parent as AccountValue).platform;
            try {
                const url = new URL(/^https?:\/\//.test(text) ? text : `https://${text}`);
                const host = url.hostname.replace(/^www\./, '');
                const matchesPlatform = PLATFORM_DOMAINS[platform].some(
                    (domain) => host === domain || host.endsWith(`.${domain}`)
                );
                // A bare domain (no profile path) is a link, not a handle — reject it even
                // when the host matches the selected platform.
                const hasProfilePath = url.pathname.replace(/\/+$/, '').length > 0;
                return matchesPlatform && hasProfilePath;
            } catch {
                return false;
            }
        }),
};

/** Validation shared by every wizard; wizard-specific rules are merged on top. */
export const baseValidationShape = {
    firstName: Yup.string().trim().required('First name is required'),
    lastName: Yup.string().trim().required('Last name is required'),
    email: Yup.string().trim().email('Enter a valid email').required('Email is required'),
    confirmEmail: Yup.string()
        .trim()
        .oneOf([Yup.ref('email')], 'Emails must match')
        .required('Confirm your email'),
    // Optional, but a provided number must be E.164 (the PhoneField builds "+<dial><digits>").
    phoneNumber: Yup.string()
        .trim()
        .matches(/^\+[1-9]\d{6,14}$/, { message: 'Enter a valid phone number', excludeEmptyString: true }),
    isOver18: Yup.boolean().oneOf([true], 'You must be 18 or over to apply'),
    locationId: Yup.number().nullable().required('Location is required'),
    accounts: Yup.array().of(Yup.object().shape(baseAccountShape)).min(1, 'Add at least one social account'),
    termsAccepted: Yup.boolean().oneOf([true], 'You must accept the terms to apply'),
};

export type StepRenderContext = {
    formik: FormikProps<SignupFormValues>;
    location: LocationValue;
    setLocation: (value: LocationValue) => void;
    setStep: (step: number) => void;
};

export type WizardStep = {
    /** Identifies a step so the review summary can jump to it without a literal index. */
    id?: string;
    title: string;
    blurb: string;
    /** Fields validated before advancing past this step. Optional steps pass an empty list. */
    fields: (keyof SignupFormValues)[];
    /** Per-account fields validated before advancing, checked across every account row. */
    accountFields?: (keyof AccountValue)[];
    render: (context: StepRenderContext) => ReactNode;
};

type Props = {
    steps: WizardStep[];
    validationSchema: ReturnType<typeof Yup.object>;
    buildBody: (values: SignupFormValues, referralCode?: string) => wave.PostCreatorApplicationBody;
};

/** Shared multi-step signup shell: progress header, step navigation, submit + result states. */
const SignupWizard = ({ steps, validationSchema, buildBody }: Props) => {
    const [step, setStep] = useState(0);
    const [submitted, setSubmitted] = useState(false);
    const [location, setLocation] = useState<LocationValue>(emptyLocationValue);
    // Captured once at mount: the hidden referral code from ?ref=<code>, never shown to the applicant.
    const [referralCode] = useState(() => new URLSearchParams(window.location.search).get('ref') ?? undefined);
    const total = steps.length;

    const handleSubmit = async (values: SignupFormValues) => {
        try {
            const response = await wave.postCreatorApplication(buildBody(values, referralCode));
            if (response.status === 201) {
                setSubmitted(true);
                return;
            }
            if (response.status === 429) {
                showNotification('Too many submissions. Please try again later.', 'error');
                return;
            }
            if (response.status === 409) {
                showNotification('It looks like you have already applied with this email.', 'error');
                return;
            }
            const detail = Object.values(response.data as Record<string, unknown>)
                .flat(Infinity)
                .filter((value): value is string => typeof value === 'string')
                .join(' ');
            showNotification(detail || `Something went wrong. If it persists, email ${SUPPORT_EMAIL}.`, 'error');
        } catch {
            showNotification(`Something went wrong. If it persists, email ${SUPPORT_EMAIL}.`, 'error');
        }
    };

    if (submitted) {
        return (
            <div className="wm-root wm-auth flex min-h-[100dvh] flex-col items-center justify-center bg-wm-bg px-5 py-12">
                <div className="wm-container text-center">
                    <WaveLogo size="md" className="mx-auto mb-6" />
                    <div className="wm-state">
                        <div className="wm-state-icon wm-state-icon--success" aria-hidden>
                            <CheckIcon />
                        </div>
                        <h1 className="wm-state__title">You&apos;re in the queue</h1>
                        <p className="wm-state__body">
                            Your application has been received. If approved you&apos;ll start receiving campaigns from
                            us, usually within a week.
                        </p>
                    </div>
                </div>
            </div>
        );
    }

    const isLastStep = step === total - 1;
    const current = steps[step];
    const progressPct = Math.round(((step + 1) / total) * 100);

    return (
        <Formik initialValues={signupInitialValues} validationSchema={validationSchema} onSubmit={handleSubmit}>
            {(formik) => {
                const goNext = async () => {
                    // Gate on this step's own fields only: the full-schema validation also
                    // reports later steps' errors (e.g. per-account sections), which must
                    // not block an earlier step.
                    const errors = await formik.validateForm();
                    const accountErrors = Array.isArray(errors.accounts) ? errors.accounts : [];
                    const stepAccountFields = current.accountFields ?? [];
                    const hasStepError =
                        current.fields.some((fieldName) => Boolean(errors[fieldName])) ||
                        stepAccountFields.some((field) =>
                            accountErrors.some(
                                (accountError) =>
                                    typeof accountError === 'object' &&
                                    Boolean((accountError as Record<string, unknown>)[field])
                            )
                        );
                    if (hasStepError) {
                        current.fields.forEach((fieldName) => formik.setFieldTouched(fieldName, true, false));
                        formik.values.accounts.forEach((_, index) =>
                            stepAccountFields.forEach((field) =>
                                formik.setFieldTouched(`accounts.${index}.${field}`, true, false)
                            )
                        );
                        return;
                    }
                    setStep((s) => Math.min(s + 1, total - 1));
                };

                return (
                    <Form className="wm-root wm-auth flex min-h-[100dvh] flex-col bg-wm-bg">
                        <header className="sticky top-0 z-10 border-b border-wm-line bg-wm-surface">
                            <div className="wm-container flex items-center justify-between px-5 py-3">
                                <WaveLogo size="sm" />
                                <span className="text-xs font-medium text-wm-ink-3">
                                    {`Step ${step + 1} of ${total}`}
                                </span>
                            </div>
                            <div className="wm-container px-5 pb-3">
                                <div className="h-1.5 overflow-hidden rounded-full bg-wm-soft">
                                    <div
                                        className="h-full rounded-full bg-wm-accent transition-all duration-300"
                                        style={{ width: `${progressPct}%` }}
                                    />
                                </div>
                            </div>
                        </header>

                        <main
                            className="wm-container flex-1 px-5 pt-6"
                            // Clears the fixed footer, which the previous padding
                            // matched almost exactly and so hid the last chip row.
                            style={{ paddingBottom: 'calc(9rem + env(safe-area-inset-bottom))' }}
                        >
                            <h1 className="wm-h1">{current.title}</h1>
                            <p className="wm-sub mb-5">{current.blurb}</p>
                            {current.render({ formik, location, setLocation, setStep })}
                        </main>

                        <footer
                            className="fixed inset-x-0 bottom-0 border-t border-wm-line bg-wm-surface"
                            style={{ paddingBottom: 'env(safe-area-inset-bottom)' }}
                        >
                            <div className="wm-container flex items-center justify-between gap-3 px-5 py-3">
                                {step > 0 ? (
                                    <button
                                        type="button"
                                        className="wm-btn wm-btn--ghost"
                                        style={{ width: 'auto' }}
                                        disabled={formik.isSubmitting}
                                        onClick={() => setStep((s) => Math.max(s - 1, 0))}
                                    >
                                        ← Back
                                    </button>
                                ) : (
                                    <span />
                                )}
                                {isLastStep ? (
                                    <button
                                        type="submit"
                                        className="wm-btn wm-btn--accent"
                                        style={{ width: 'auto' }}
                                        disabled={formik.isSubmitting}
                                    >
                                        {formik.isSubmitting ? 'Submitting…' : 'Submit application →'}
                                    </button>
                                ) : (
                                    <button
                                        type="button"
                                        className="wm-btn wm-btn--accent"
                                        style={{ width: 'auto' }}
                                        onClick={goNext}
                                    >
                                        Continue →
                                    </button>
                                )}
                            </div>
                        </footer>
                    </Form>
                );
            }}
        </Formik>
    );
};

export default SignupWizard;
